import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  Paper,
  Grid,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ADCalendarPriceComponent from "./ADCalendarPriceComponent";

/** Top option: choosing it keeps the venue that was selected when the dropdown opened (or blank). */
const PLACEHOLDER_VENUE = { id: "__select_venue__", isPlaceholder: true };

function venueDisplayLabel(v) {
  if (!v || v.isPlaceholder) return "Select Venue";
  const cityPart = v.city || "";
  const hasSite = v.site != null && v.site !== "";
  const citySite = hasSite ? `${cityPart}${v.site}` : cityPart;
  return citySite || v.address || v.id || "";
}

const WORKFLOW_TITLE = {
  pending: "Pending",
  notified: "Notified",
  confirmed: "Confirmed",
};

/** Strip trailing " (Pending)" etc. so search still matches city when input shows status. */
function stripWorkflowSuffixFromInput(input) {
  return String(input || "")
    .replace(/\s*\((Pending|Notified|Confirmed)\)\s*$/i, "")
    .trim();
}

function formatWorkflowSuffix(status) {
  if (status == null || status === "") return "";
  const key = String(status).toLowerCase();
  const title = WORKFLOW_TITLE[key];
  if (!title) return "";
  return ` (${title})`;
}

function venueDisplayLabelWithStatus(v, statusById) {
  const base = venueDisplayLabel(v);
  if (!v || v.isPlaceholder || !statusById) return base;
  const st = statusById[v.id];
  if (st == null || st === "") return base;
  return `${base}${formatWorkflowSuffix(st)}`;
}

function filterVenuesByQuery(venues, input) {
  const q = stripWorkflowSuffixFromInput(input).toLowerCase();
  if (!q) return venues;
  const tokens = q.split(/\s+/).filter(Boolean);
  return venues.filter((v) => {
    const hay = `${v.city || ""}${v.site != null ? v.site : ""} ${v.address || ""} ${v.id || ""}`.toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
}

const ADSearchForm = ({
  handleReset,
  handleSearch,
  venues,
  setCurrentvenue,
  selectedVenue,
  setSelectedVenue,
  showNotifyButton = false,
  onNotify,
  notifyDisabled = false,
  /** AHA only: map venueId -> pending | notified | confirmed */
  venueWorkflowStatusById = null,
  showConfirmButton = false,
  onConfirm,
  confirmDisabled = false,
  showPriceTable = true,
  ahaBlsCourseCount = null,
}) => {
  const snapshotVenueIdRef = useRef(selectedVenue);
  const [inputValue, setInputValue] = useState("");
  const prevSelectedVenueRef = useRef(selectedVenue);
  /** Remount listbox when crossing empty ↔ non-empty so MUI listbox never keeps stale rows (duplicate blocks after clear + retype). */
  const [listboxEpoch, setListboxEpoch] = useState(0);
  const wasQueryEmptyRef = useRef(!String(inputValue).trim());

  const sortedVenues = useMemo(() => {
    if (!Array.isArray(venues)) return [];
    const sorted = [...venues].sort((a, b) => {
      const c = String(a.city || "").localeCompare(String(b.city || ""));
      if (c !== 0) return c;
      const sa = a.site != null ? Number(a.site) : Number.NEGATIVE_INFINITY;
      const sb = b.site != null ? Number(b.site) : Number.NEGATIVE_INFINITY;
      return sa - sb;
    });
    const seen = new Set();
    return sorted.filter((v) => {
      const id = v.id;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [venues]);

  const selectedVenueObj =
    sortedVenues.find((v) => v.id === selectedVenue) || null;

  /** AHA: Confirm only after Notify has moved status from pending → notified. */
  const confirmDisabledUntilNotified = useMemo(() => {
    if (!showConfirmButton || !venueWorkflowStatusById || !selectedVenue) {
      return false;
    }
    const st = venueWorkflowStatusById[selectedVenue];
    return String(st || "").toLowerCase() === "pending";
  }, [showConfirmButton, venueWorkflowStatusById, selectedVenue]);

  const sortedVenuesRef = useRef(sortedVenues);
  sortedVenuesRef.current = sortedVenues;

  // Single source of truth for the list: only inputValue + sortedVenues (no ref) so results never mix with a previous query.
  const filteredVenueOptions = useMemo(() => {
    const filtered = filterVenuesByQuery(sortedVenues, inputValue);
    return [PLACEHOLDER_VENUE, ...filtered];
  }, [sortedVenues, inputValue]);

  // Sync input when selection id changes, or when venues load and input is still empty (cache restore).
  useEffect(() => {
    if (selectedVenue !== prevSelectedVenueRef.current) {
      prevSelectedVenueRef.current = selectedVenue;
      if (!selectedVenue) {
        setInputValue("");
        return;
      }
      const v = sortedVenues.find((x) => x.id === selectedVenue);
      if (v) {
        setInputValue(venueDisplayLabelWithStatus(v, venueWorkflowStatusById));
      }
      return;
    }
    if (selectedVenue && sortedVenues.length > 0) {
      const v = sortedVenues.find((x) => x.id === selectedVenue);
      if (v) {
        setInputValue((prev) => {
          if (prev !== "") {
            return prev;
          }
          return venueDisplayLabelWithStatus(v, venueWorkflowStatusById);
        });
      }
    }
  }, [selectedVenue, sortedVenues, venueWorkflowStatusById]);

  /** After notify/confirm or initial workflow fetch: refresh bracket text without depending on sortedVenues identity. */
  useEffect(() => {
    if (!selectedVenue || !venueWorkflowStatusById) return;
    const v = sortedVenuesRef.current.find((x) => x.id === selectedVenue);
    if (!v) return;
    setInputValue(venueDisplayLabelWithStatus(v, venueWorkflowStatusById));
  }, [venueWorkflowStatusById, selectedVenue]);

  useEffect(() => {
    const empty = !String(inputValue).trim();
    if (empty !== wasQueryEmptyRef.current) {
      wasQueryEmptyRef.current = empty;
      setListboxEpoch((n) => n + 1);
    }
  }, [inputValue]);

  const passThroughFilterOptions = useCallback((options) => options, []);

  const handleVenueAutocompleteChange = (event, newValue) => {
    if (newValue && newValue.isPlaceholder) {
      const id = snapshotVenueIdRef.current ?? "";
      setSelectedVenue(id);
      setCurrentvenue(id ? sortedVenues.find((v) => v.id === id) || "" : "");
      const v = id ? sortedVenues.find((x) => x.id === id) : null;
      const label = v ? venueDisplayLabelWithStatus(v, venueWorkflowStatusById) : "";
      setInputValue(label);
      return;
    }
    if (!newValue) {
      setSelectedVenue("");
      setCurrentvenue("");
      setInputValue("");
      return;
    }
    setSelectedVenue(newValue.id);
    setCurrentvenue(newValue);
    setInputValue(venueDisplayLabelWithStatus(newValue, venueWorkflowStatusById));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Autocomplete
          sx={{ minWidth: 320, flex: "1 1 480px", maxWidth: 900, mr: 1 }}
          options={filteredVenueOptions}
          value={selectedVenueObj}
          onOpen={() => {
            snapshotVenueIdRef.current = selectedVenue;
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue ?? "");
          }}
          filterOptions={passThroughFilterOptions}
          getOptionKey={(option) =>
            option?.isPlaceholder ? "__select_venue__" : String(option.id)
          }
          getOptionLabel={(o) => venueDisplayLabelWithStatus(o, venueWorkflowStatusById)}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          ListboxProps={{ key: `venue-lb-${listboxEpoch}` }}
          freeSolo={false}
          disableClearable={false}
          onChange={handleVenueAutocompleteChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Venue"
              placeholder="Search venues…"
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                {option.isPlaceholder ? (
                  <Typography variant="body2" color="text.secondary">
                    Select Venue
                  </Typography>
                ) : (
                  <Typography variant="body2" component="span">
                    {venueDisplayLabelWithStatus(option, venueWorkflowStatusById)}
                  </Typography>
                )}
              </li>
            );
          }}
          noOptionsText="No matching venues"
        />

        <Button variant="contained" color="primary" onClick={handleSearch} sx={{ mr: 1 }}>
          Search
        </Button>
        <Button variant="outlined" onClick={handleReset} sx={{ mr: 1 }}>
          Reset
        </Button>
        {showNotifyButton && onNotify ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={onNotify}
            disabled={notifyDisabled}
            sx={{ mr: showConfirmButton ? 1 : 0 }}
          >
            Notify
          </Button>
        ) : null}
        {showConfirmButton && onConfirm ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirm}
            disabled={confirmDisabled || confirmDisabledUntilNotified}
            title={
              confirmDisabledUntilNotified
                ? "This venue is still Pending. Use Notify first; Confirm is available after status becomes Notified."
                : undefined
            }
          >
            Confirm
          </Button>
        ) : null}
      </Box>

      {showPriceTable ? (
        <ADCalendarPriceComponent selectedVenue={selectedVenue} />
      ) : selectedVenue ? (
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          mb={3}
          width="100%"
        >
          <Paper elevation={3} sx={{ padding: 2, width: "100%" }}>
            <Grid container spacing={1} direction="column" alignItems="center">
              <Grid item>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  fontFamily="Arial"
                  textAlign="center"
                >
                AHA BLS Class
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h4" textAlign="center" component="p" sx={{ m: 0 }}>
                  {typeof ahaBlsCourseCount === "number" ? ahaBlsCourseCount : "—"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      ) : null}
    </LocalizationProvider>
  );
};

export default ADSearchForm;
